import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Tab,
  Tabs,
} from '@nextui-org/react';
import { useEffect, useMemo, useState } from 'react';
import {
  getAssistantPrompts,
  getTrafficLightPrompt,
} from '../../store/assistant/assistantThunks';
import { PromptType } from '../../types';
import MarkdownWrapper from '../base/MarkdownWrapper';
import { GENERIC_APP } from '../../config/generic';
import { SOS_APP } from '../../config/sos';
import { selectAssistantConfig } from '../../store/settings/settingsSlice';
import { useAppSelector } from '../../store/hooks';
import { BASE_APP } from '../../config/base';
import { TRAINED_MODEL_VALUES } from '../../util';

export interface PromptModalProps {
  isOpen: boolean;
  onOpenChange: () => void;
}

export default function PromptModal({
  isOpen,
  onOpenChange,
}: PromptModalProps) {
  const [prompts, setPrompts] = useState<
    { promptType: PromptType; prompt: string }[]
  >([]);

  const [tlPrompt, setTLPrompt] = useState<{
    prompt_type: string;
    prompt: string;
  }>();

  const assistantConfig = useAppSelector(selectAssistantConfig);
  const selectedAssistantModel = assistantConfig.modelTag;

  const filteredPrompts = useMemo(() => {
    let assistantFunctions = BASE_APP.assistantFunctions;
    if (process.env.REACT_APP_DEPLOYMENT_TYPE === 'SOS') {
      assistantFunctions = assistantFunctions.concat(
        SOS_APP.assistantFunctions
      );
    } else {
      assistantFunctions = assistantFunctions.concat(
        GENERIC_APP.assistantFunctions
      );
    }

    let filteredArray: { promptType: PromptType; prompt: string }[] = [];
    assistantFunctions.forEach((assistantFunction) => {
      prompts.forEach((prompt) => {
        if (prompt.promptType === assistantFunction) {
          filteredArray.push(prompt);
        }
      });
    });

    if (process.env.REACT_APP_DEPLOYMENT_TYPE === 'SOS') {
      filteredArray.push({
        // @ts-ignore
        promptType: tlPrompt?.prompt_type,
        prompt: tlPrompt?.prompt ? tlPrompt?.prompt : '',
      });
    }

    return filteredArray;
  }, [prompts, tlPrompt]);

  const loadPrompts = () => {
    setPrompts([]);
    getTrafficLightPrompt().then((prompt) => {
      prompt && setTLPrompt(prompt);
    });
    getAssistantPrompts()
      .then((dtos) =>
        dtos?.map((dto) => ({
          promptType: dto.prompt_type,
          prompt: dto.prompt,
        }))
      )
      .then((newPrompts) => {
        if (TRAINED_MODEL_VALUES.includes(selectedAssistantModel)) {
          newPrompts?.forEach((p) => {
            if (p.promptType === 'generate_response')
              newPrompts.forEach((prompt) => {
                if (prompt.promptType === 'generate_response_trained') {
                  p.prompt = prompt.prompt;
                }
              });
          });
        }

        newPrompts && setPrompts(newPrompts);
      });
  };

  useEffect(loadPrompts, [selectedAssistantModel]);

  let featureSettings = BASE_APP.featureSettings;
  if (process.env.REACT_APP_DEPLOYMENT_TYPE === 'SOS') {
    featureSettings = featureSettings.concat(SOS_APP.featureSettings);
  } else {
    featureSettings = featureSettings.concat(GENERIC_APP.featureSettings);
  }

  const tabs = useMemo(
    () =>
      filteredPrompts.length ? (
        <Tabs aria-label="Prompt tabs" items={filteredPrompts} radius="full">
          {(item) => (
            <Tab
              key={item.promptType}
              title={(
                featureSettings.find((s) => s.key === item.promptType)?.label ??
                item.promptType
              )?.replace(/^./, (c) => c.toUpperCase())}
            >
              <Card>
                <CardBody>
                  <MarkdownWrapper>{item.prompt}</MarkdownWrapper>
                </CardBody>
              </Card>
            </Tab>
          )}
        </Tabs>
      ) : (
        <p className="italic">No prompts.</p>
      ),
    [filteredPrompts, featureSettings]
  );

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      size="3xl"
      scrollBehavior="inside"
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader>Prompt Viewer</ModalHeader>
            <ModalBody className="border-y-1">{tabs}</ModalBody>
            <ModalFooter>
              <ButtonGroup>
                <Button
                  variant="flat"
                  isLoading={!filteredPrompts.length}
                  onPress={loadPrompts}
                >
                  Reload prompts
                </Button>
                <Button color="primary" onPress={onClose}>
                  Close
                </Button>
              </ButtonGroup>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
